import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, X, Camera, FileText, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button.js";
import { Badge } from "@/components/ui/badge.js";
import { useSnapToField } from "./SnapToFieldLayer.js";

export type SuggestionEvidence =
  | { type: "photo"; media_id?: string; reasoning: string }
  | { type: "intake_note"; note_excerpt?: string; reasoning: string }
  | { type: "absent"; reasoning: string };

export type Suggestion = {
  id: string;
  field_key: string;
  field_label: string;
  suggested_value: unknown;
  confidence: number;
  source_evidence: SuggestionEvidence;
};

type Props = {
  suggestion: Suggestion;
  onAccept: (s: Suggestion) => Promise<void> | void;
  onReject: (s: Suggestion) => Promise<void> | void;
  /** When true, the Accept button is disabled (e.g., viewer role). */
  readOnly?: boolean;
};

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "number") return String(v);
  if (typeof v === "boolean") return v ? "Sì" : "No";
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

export function AISuggestionCard({
  suggestion: s,
  onAccept,
  onReject,
  readOnly,
}: Props): JSX.Element {
  const ref = useRef<HTMLElement>(null);
  const { fly } = useSnapToField();
  const [busy, setBusy] = useState<"accept" | "reject" | null>(null);

  const Icon =
    s.source_evidence.type === "photo"
      ? Camera
      : s.source_evidence.type === "intake_note"
        ? FileText
        : HelpCircle;

  const confTone: "success" | "ai" | "warn" =
    s.confidence >= 0.85 ? "success" : s.confidence >= 0.6 ? "ai" : "warn";

  async function handleAccept(): Promise<void> {
    if (busy || readOnly) return;
    setBusy("accept");
    const rect = ref.current?.getBoundingClientRect();
    if (rect) {
      await fly(
        s.field_key,
        { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
        s.field_label,
      );
    }
    await onAccept(s);
    setBusy(null);
  }

  async function handleReject(): Promise<void> {
    if (busy) return;
    setBusy("reject");
    await onReject(s);
    setBusy(null);
  }

  return (
    <motion.article
      ref={ref}
      layout
      exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.18 } }}
      className="rounded border border-rule bg-surface p-4"
      data-field-key={s.field_key}
    >
      <header className="mb-2 flex items-start justify-between gap-2">
        <div className="space-y-1">
          <p className="font-mono text-meta uppercase tracking-wider text-ink-mute">
            {s.field_key}
          </p>
          <p className="text-body font-semibold text-ink">{s.field_label}</p>
        </div>
        <Badge tone={confTone}>IA · {s.confidence.toFixed(2)}</Badge>
      </header>

      <p className="mb-3 break-words rounded border border-rule bg-surface-2 px-3 py-2 font-mono text-body text-ink">
        {formatValue(s.suggested_value)}
      </p>

      <div className="mb-3 flex items-start gap-2 text-body text-ink-soft">
        <Icon className="mt-0.5 size-3.5 shrink-0 text-ai" aria-hidden />
        <p className="text-meta">{s.source_evidence.reasoning}</p>
      </div>

      <div className="flex gap-2">
        <Button
          size="sm"
          variant="primary"
          onClick={handleAccept}
          disabled={busy !== null || readOnly}
        >
          <Check className="size-3.5" /> Accetta
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleReject}
          disabled={busy !== null}
        >
          <X className="size-3.5" /> Rifiuta
        </Button>
      </div>
    </motion.article>
  );
}
